import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStore } from '../context/StoreContext';
import { DEFAULT_NOTE_COLORS, LARGE_FILE_WARNING_MB, MAX_FILE_MB } from '../constants';
import SafeIcon from '../common/SafeIcon';
import { processAttachment, formatFileSize } from '../utils/attachment';
import { uploadAttachmentSilently } from '../utils/cloudAttachment';
import { dbGet } from '../utils/db';
import { plainTextToHtml, sanitizeRichHtml } from '../utils/richText';
import TagSuggestions from './TagSuggestions';

const AUTOSAVE_DEBOUNCE_MS = 450;
const FONT_FAMILIES = ['Arial', 'Georgia', 'Times New Roman', 'Verdana', 'Courier New'];
const FONT_SIZES = [10, 12, 14, 16, 18, 24, 32];
const MB = 1024 * 1024;

const ToolbarButton = ({ label, title, onAction, children }) => (
  <button
    type="button"
    title={title || label}
    aria-label={title || label}
    onMouseDown={e => e.preventDefault()}
    onClick={onAction}
    className="h-11 min-w-11 px-2.5 rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:text-gray-900 text-sm font-bold flex items-center justify-center"
  >
    {children || label}
  </button>
);

const Editor = () => {
  const {
    editingNote, setEditingNote, saveNote, archiveNote,
    deleteNotePermanently, focusMode, setSaveStatus, runSync
  } = useStore();

  const [localNote, setLocalNote] = useState(null);
  const [attachmentNotice, setAttachmentNotice] = useState(null);
  const localNoteRef = useRef(null);
  const titleRef = useRef(null);
  const editorRef = useRef(null);
  const saveTimerRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const openNoteIdRef = useRef(null);
  const savedRangeRef = useRef(null);

  useEffect(() => {
    const nextId = editingNote?.id || null;
    if (nextId && nextId !== openNoteIdRef.current) {
      openNoteIdRef.current = nextId;
      localNoteRef.current = editingNote;
      setLocalNote(editingNote);
      setAttachmentNotice(null);
      const focusTimer = setTimeout(() => titleRef.current?.focus(), 120);
      return () => clearTimeout(focusTimer);
    }
    if (!nextId) {
      openNoteIdRef.current = null;
      localNoteRef.current = null;
      setLocalNote(null);
      setAttachmentNotice(null);
    }
  }, [editingNote?.id]);

  useEffect(() => {
    if (!localNote || localNote.type !== 'text' || !editorRef.current) return;
    const initialHtml = sanitizeRichHtml(localNote.bodyHtml || plainTextToHtml(localNote.bodyText || ''));
    editorRef.current.innerHTML = initialHtml;
  }, [localNote?.id, localNote?.type]);

  const handleClose = useCallback(async () => {
    clearTimeout(saveTimerRef.current);
    const latest = localNoteRef.current || localNote;
    if (latest) {
      await saveNote(latest, false);
      await runSync('exit_editor');
    }
    setEditingNote(null);
  }, [localNote, runSync, saveNote, setEditingNote]);

  useEffect(() => {
    const onDone = () => handleClose();
    window.addEventListener('stickyhq:editor-done', onDone);
    return () => window.removeEventListener('stickyhq:editor-done', onDone);
  }, [handleClose]);

  useEffect(() => () => {
    clearTimeout(saveTimerRef.current);
    clearTimeout(noticeTimerRef.current);
  }, []);

  const showAttachmentNotice = (message, tone = 'warning') => {
    clearTimeout(noticeTimerRef.current);
    setAttachmentNotice({ message, tone });
    noticeTimerRef.current = setTimeout(() => setAttachmentNotice(null), 8000);
  };

  const handleChange = (updates) => {
    const base = localNoteRef.current;
    if (!base) return;
    const next = { ...base, ...updates };
    localNoteRef.current = next;
    setLocalNote(next);
    clearTimeout(saveTimerRef.current);
    setSaveStatus('saving');
    saveTimerRef.current = setTimeout(() => saveNote(next, true), AUTOSAVE_DEBOUNCE_MS);
    return next;
  };

  const saveSelection = () => {
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount || !editorRef.current) return;
    const range = selection.getRangeAt(0);
    if (editorRef.current.contains(range.commonAncestorContainer)) savedRangeRef.current = range.cloneRange();
  };

  const restoreSelection = () => {
    const range = savedRangeRef.current;
    if (!range) return;
    const selection = window.getSelection?.();
    if (!selection) return;
    editorRef.current?.focus();
    selection.removeAllRanges();
    selection.addRange(range);
  };

  const commitRichText = () => {
    if (!editorRef.current) return;
    const bodyHtml = sanitizeRichHtml(editorRef.current.innerHTML);
    const bodyText = (editorRef.current.innerText || '').replace(/\n{3,}/g, '\n\n').trimEnd();
    handleChange({ bodyHtml, bodyText });
  };

  const execFormat = (command, value = null) => {
    restoreSelection();
    document.execCommand(command, false, value);
    saveSelection();
    commitRichText();
  };

  const applyFontSize = (px) => {
    restoreSelection();
    document.execCommand('fontSize', false, '7');
    editorRef.current?.querySelectorAll('font[size="7"]').forEach(el => {
      el.removeAttribute('size');
      el.style.fontSize = `${px}px`;
    });
    saveSelection();
    commitRichText();
  };

  const patchUploadedAttachment = async (noteId, attachmentId, cloudAttachment) => {
    if (!cloudAttachment?.driveFileId) return;
    const openNote = localNoteRef.current?.id === noteId ? localNoteRef.current : null;
    const source = openNote || await dbGet('notes', noteId);
    if (!source) return;
    const attachments = (source.attachments || []).map(att => att.id === attachmentId ? cloudAttachment : att);
    if (openNote) handleChange({ attachments });
    else await saveNote({ ...source, attachments }, true);
  };

  const ingestFiles = async (files) => {
    const noteId = localNoteRef.current?.id;
    if (!noteId) return;

    const incoming = Array.from(files || []);
    const allowedFiles = incoming.filter(file => file.size <= MAX_FILE_MB * MB);
    const rejectedFiles = incoming.filter(file => file.size > MAX_FILE_MB * MB);
    const largeFiles = allowedFiles.filter(file => file.size > LARGE_FILE_WARNING_MB * MB);

    if (rejectedFiles.length) {
      const first = rejectedFiles[0];
      showAttachmentNotice(`${first.name || 'File'} is ${formatFileSize(first.size)} and exceeds the ${MAX_FILE_MB} MB limit.`, 'error');
    } else if (largeFiles.length) {
      const first = largeFiles[0];
      const more = largeFiles.length > 1 ? ` + ${largeFiles.length - 1} more` : '';
      showAttachmentNotice(`Large file: ${first.name || 'attachment'} (${formatFileSize(first.size)})${more}. It will still be attached; the silent upload may take longer.`, 'warning');
    }

    const accepted = [];
    for (const file of allowedFiles) {
      try {
        accepted.push(await processAttachment(file));
      } catch (err) {
        console.warn('Attachment skipped', file?.name, err);
        showAttachmentNotice(`${file?.name || 'Attachment'} could not be added. ${err.message || ''}`.trim(), 'error');
      }
    }
    if (!accepted.length) return;

    const current = localNoteRef.current;
    if (!current || current.id !== noteId) return;
    const next = handleChange({ attachments: [...(current.attachments || []), ...accepted] });

    // Persist locally without starting normal note sync while raw attachment bytes
    // are still present. Drive metadata will trigger normal sync after upload.
    clearTimeout(saveTimerRef.current);
    if (next) await saveNote(next, false);

    // Upload to Drive after the local attachment is already visible. No app-level
    // progress modal or blocking popup is shown.
    void (async () => {
      for (const attachment of accepted) {
        const cloudAttachment = await uploadAttachmentSilently(attachment, noteId);
        await patchUploadedAttachment(noteId, attachment.id, cloudAttachment);
      }
    })();
  };

  const handleFileInput = async (e) => {
    await ingestFiles(e.target.files);
    e.target.value = '';
  };

  const handlePaste = async (e) => {
    const fileItems = Array.from(e.clipboardData?.items || [])
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean);

    if (fileItems.length) {
      e.preventDefault();
      await ingestFiles(fileItems);
      return;
    }

    if (localNote.type === 'text') {
      const html = e.clipboardData?.getData('text/html');
      if (html) {
        e.preventDefault();
        execFormat('insertHTML', sanitizeRichHtml(html));
      }
    }
  };

  const handleDrop = async (e) => {
    const files = Array.from(e.dataTransfer?.files || []);
    if (!files.length) return;
    e.preventDefault();
    await ingestFiles(files);
  };

  const removeAttachment = (id) => handleChange({ attachments: (localNoteRef.current?.attachments || []).filter(a => a.id !== id) });
  const updateAttachment = (id, updates) => handleChange({ attachments: (localNoteRef.current?.attachments || []).map(a => a.id === id ? { ...a, ...updates } : a) });

  const downloadAttachment = (att) => {
    if (att.dataRef) {
      const a = document.createElement('a');
      a.href = att.dataRef;
      a.download = att.name || 'attachment';
      document.body.appendChild(a);
      a.click();
      a.remove();
      return;
    }
    if (att.driveFileId) {
      window.open(`https://drive.google.com/uc?export=download&id=${encodeURIComponent(att.driveFileId)}`, '_blank', 'noopener,noreferrer');
      return;
    }
    if (att.driveUrl) window.open(att.driveUrl, '_blank', 'noopener,noreferrer');
  };

  const ensureTable = () => localNote.table || {
    columns: [{ id: 'col-1', name: 'Column 1' }, { id: 'col-2', name: 'Column 2' }],
    rows: [{ id: `row-${Date.now()}`, cells: { 'col-1': '', 'col-2': '' } }]
  };

  if (!localNote) return null;
  const table = ensureTable();

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className={`native-editor-overlay fixed inset-0 z-40 bg-gray-900/20 backdrop-blur-sm flex justify-end ${focusMode ? 'justify-center items-center min-[1100px]:p-8' : ''}`} onClick={handleClose}>
        <motion.div
          initial={focusMode ? { scale: 0.95, opacity: 0 } : { x: '100%' }}
          animate={focusMode ? { scale: 1, opacity: 1 } : { x: 0 }}
          exit={focusMode ? { scale: 0.95, opacity: 0 } : { x: '100%' }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className={`native-editor-panel bg-white shadow-2xl flex flex-col min-w-0 ${focusMode ? 'w-full max-w-5xl h-full min-[1100px]:rounded-2xl' : 'w-full md:w-[calc(100vw-88px)] min-[1100px]:w-full max-w-3xl h-full md:rounded-l-3xl'}`}
          onClick={e => e.stopPropagation()}
          onPaste={handlePaste}
          onDragOver={e => { if (e.dataTransfer?.types?.includes('Files')) e.preventDefault(); }}
          onDrop={handleDrop}
        >
          <div className="flex items-center justify-between gap-2 px-3 py-3 md:p-4 border-b border-gray-100 shrink-0 overflow-x-auto no-scrollbar">
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => handleChange({ pinned: !localNote.pinned })} className="min-w-11 min-h-11 p-2 hover:bg-gray-100 rounded-xl text-gray-400 hover:text-yellow-500 transition" aria-label={localNote.pinned ? 'Unpin note' : 'Pin note'}>
                <SafeIcon name="Star" className={localNote.pinned ? 'fill-current text-yellow-500' : ''} />
              </button>
              <div className="flex gap-1 bg-gray-50 p-1 rounded-lg">
                {DEFAULT_NOTE_COLORS.map(c => (
                  <button key={c.key} onClick={() => handleChange({ colorKey: c.key })} className={`w-8 h-8 rounded-full border-2 transition-transform ${localNote.colorKey === c.key ? 'border-gray-800 scale-110' : 'border-transparent hover:scale-110'}`} style={{ backgroundColor: c.hex }} title={c.label} aria-label={`Set ${c.label} color`} />
                ))}
              </div>
            </div>

            <div className="flex items-center gap-1 md:gap-2 shrink-0">
              <label className="cursor-pointer min-w-11 min-h-11 p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition flex items-center justify-center" title={`Attach files, documents, or screenshots (up to ${MAX_FILE_MB} MB)`}>
                <SafeIcon name="Paperclip" />
                <input type="file" multiple className="hidden" onChange={handleFileInput} />
              </label>
              <button onClick={() => { if (localNote.archivedAt) archiveNote(localNote.id, true); else archiveNote(localNote.id); setEditingNote(null); }} className="min-w-11 min-h-11 p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition" title={localNote.archivedAt ? 'Restore' : 'Archive'}>
                <SafeIcon name={localNote.archivedAt ? 'RotateCcw' : 'Archive'} />
              </button>
              <button onClick={() => { deleteNotePermanently(localNote.id); setEditingNote(null); }} className="min-w-11 min-h-11 p-2 hover:bg-red-50 text-red-400 rounded-xl transition" title="Delete Permanently">
                <SafeIcon name="Trash2" />
              </button>
              <button onClick={handleClose} className="hidden min-[1100px]:block min-w-11 min-h-11 p-2 hover:bg-gray-100 rounded-xl text-gray-500 transition ml-1"><SafeIcon name="X" /></button>
            </div>
          </div>

          <AnimatePresence>
            {attachmentNotice && (
              <motion.div
                initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                className={`mx-3 mt-3 md:mx-4 px-4 py-3 rounded-xl border flex items-start gap-3 text-sm shrink-0 ${attachmentNotice.tone === 'error' ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-900'}`}
              >
                <SafeIcon name={attachmentNotice.tone === 'error' ? 'AlertCircle' : 'AlertTriangle'} className="mt-0.5 shrink-0" />
                <span className="flex-1 font-medium leading-relaxed">{attachmentNotice.message}</span>
                <button type="button" onClick={() => setAttachmentNotice(null)} className="p-1 rounded-lg hover:bg-black/5" aria-label="Dismiss file notice"><SafeIcon name="X" /></button>
              </motion.div>
            )}
          </AnimatePresence>

          {localNote.type === 'text' && (
            <div className="border-b border-gray-100 px-3 py-2 md:px-4 bg-gray-50/70 overflow-x-auto no-scrollbar shrink-0">
              <div className="flex items-center gap-1 min-w-max">
                <select defaultValue="Arial" onMouseDown={saveSelection} onChange={e => execFormat('fontName', e.target.value)} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 outline-none" aria-label="Font family">
                  {FONT_FAMILIES.map(font => <option key={font} value={font}>{font}</option>)}
                </select>
                <select defaultValue="16" onMouseDown={saveSelection} onChange={e => applyFontSize(Number(e.target.value))} className="h-11 px-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-700 outline-none" aria-label="Font size">
                  {FONT_SIZES.map(size => <option key={size} value={size}>{size}</option>)}
                </select>
                <div className="w-px h-6 bg-gray-200 mx-1" />
                <ToolbarButton label="B" title="Bold" onAction={() => execFormat('bold')}><span className="font-black">B</span></ToolbarButton>
                <ToolbarButton label="I" title="Italic" onAction={() => execFormat('italic')}><span className="italic font-serif">I</span></ToolbarButton>
                <ToolbarButton label="U" title="Underline" onAction={() => execFormat('underline')}><span className="underline">U</span></ToolbarButton>
                <div className="w-px h-6 bg-gray-200 mx-1" />
                <ToolbarButton title="Bulleted list" onAction={() => execFormat('insertUnorderedList')}><SafeIcon name="List" /></ToolbarButton>
                <ToolbarButton title="Numbered list" onAction={() => execFormat('insertOrderedList')}><span className="font-mono">1.</span></ToolbarButton>
                <ToolbarButton title="Decrease indent" onAction={() => execFormat('outdent')}><span>←|</span></ToolbarButton>
                <ToolbarButton title="Increase indent" onAction={() => execFormat('indent')}><span>|→</span></ToolbarButton>
                <div className="w-px h-6 bg-gray-200 mx-1" />
                <ToolbarButton title="Undo" onAction={() => execFormat('undo')}><SafeIcon name="CornerUpLeft" /></ToolbarButton>
                <ToolbarButton title="Redo" onAction={() => execFormat('redo')}><SafeIcon name="CornerUpRight" /></ToolbarButton>
                <ToolbarButton title="Clear formatting" onAction={() => execFormat('removeFormat')}><SafeIcon name="XCircle" /></ToolbarButton>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-4 py-5 md:p-8 max-w-4xl mx-auto w-full min-w-0">
            <input ref={titleRef} value={localNote.title || ''} onChange={e => handleChange({ title: e.target.value })} placeholder="Note Title" className="w-full text-3xl md:text-4xl font-bold bg-transparent border-none focus:outline-none focus:ring-0 text-gray-900 mb-5 md:mb-6 placeholder-gray-300" />

            <div className="flex items-center gap-2 mb-2 flex-wrap">
              {(localNote.tags || []).map(tag => (
                <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-md text-sm flex items-center gap-1 group">
                  #{tag}
                  <button onClick={() => handleChange({ tags: localNote.tags.filter(t => t !== tag) })} className="md:opacity-0 md:group-hover:opacity-100 transition text-gray-400 hover:text-red-500"><SafeIcon name="X" className="w-3 h-3" /></button>
                </span>
              ))}
              <input placeholder="Add tag..." className="text-sm bg-transparent border-none focus:outline-none text-gray-500 placeholder-gray-300 w-28" onKeyDown={e => { if (e.key === 'Enter' && e.currentTarget.value.trim()) { e.preventDefault(); handleChange({ tags: [...new Set([...(localNote.tags || []), e.currentTarget.value.trim()])] }); e.currentTarget.value = ''; } }} />
            </div>

            <TagSuggestions note={localNote} onAddTag={tag => handleChange({ tags: [...new Set([...(localNote.tags || []), tag])] })} />

            <div className="mt-7 md:mt-8 min-w-0">
              {localNote.type === 'text' && (
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  data-placeholder="Type or paste anything here…"
                  className="rich-note-editor w-full text-gray-700 text-base md:text-lg"
                  onInput={() => { saveSelection(); commitRichText(); }}
                  onKeyUp={saveSelection}
                  onMouseUp={saveSelection}
                  onFocus={saveSelection}
                  onBlur={() => {
                    if (!editorRef.current) return;
                    const cleaned = sanitizeRichHtml(editorRef.current.innerHTML);
                    if (cleaned !== editorRef.current.innerHTML) editorRef.current.innerHTML = cleaned;
                    commitRichText();
                  }}
                />
              )}

              {localNote.type === 'checklist' && (
                <div className="space-y-2">
                  {(localNote.checklistItems || []).map((item, idx) => (
                    <div key={item.id} className="flex items-center gap-3 group">
                      <button onClick={() => { const newItems = [...localNote.checklistItems]; newItems[idx] = { ...newItems[idx], done: !newItems[idx].done }; handleChange({ checklistItems: newItems }); }} className={`w-11 h-11 rounded-xl border flex items-center justify-center transition shrink-0 ${item.done ? 'bg-blue-500 border-blue-500 text-white' : 'border-gray-300 text-transparent hover:border-blue-400'}`}><SafeIcon name="Check" className="w-5 h-5" /></button>
                      <input value={item.text} onChange={e => { const newItems = [...localNote.checklistItems]; newItems[idx] = { ...newItems[idx], text: e.target.value }; handleChange({ checklistItems: newItems }); }} className={`flex-1 min-w-0 bg-transparent border-none focus:outline-none text-base md:text-lg ${item.done ? 'text-gray-400 line-through' : 'text-gray-700'}`} />
                      <button onClick={() => handleChange({ checklistItems: localNote.checklistItems.filter((_, i) => i !== idx) })} className="md:opacity-0 md:group-hover:opacity-100 text-gray-400 hover:text-red-500 transition"><SafeIcon name="X" /></button>
                    </div>
                  ))}
                  <button onClick={() => handleChange({ checklistItems: [...(localNote.checklistItems || []), { id: `ci-${Date.now()}`, text: '', done: false }] })} className="flex items-center gap-2 text-gray-400 hover:text-blue-500 transition mt-4 py-2"><SafeIcon name="Plus" /> Add Item</button>
                </div>
              )}

              {localNote.type === 'table' && (
                <div className="space-y-4 min-w-0">
                  <div className="overflow-x-auto border border-gray-200 rounded-xl">
                    <table className="w-full min-w-[520px] border-collapse text-sm">
                      <thead><tr className="bg-gray-50">{table.columns.map(col => (
                        <th key={col.id} className="border-b border-r border-gray-200 p-2 last:border-r-0"><input value={col.name} onChange={e => handleChange({ table: { ...table, columns: table.columns.map(c => c.id === col.id ? { ...c, name: e.target.value } : c) } })} className="w-full bg-transparent font-bold outline-none" /></th>
                      ))}</tr></thead>
                      <tbody>{table.rows.map(row => (
                        <tr key={row.id}>{table.columns.map(col => (
                          <td key={col.id} className="border-b border-r border-gray-100 p-2 last:border-r-0"><input value={row.cells?.[col.id] || ''} onChange={e => handleChange({ table: { ...table, rows: table.rows.map(r => r.id === row.id ? { ...r, cells: { ...(r.cells || {}), [col.id]: e.target.value } } : r) } })} className="w-full bg-transparent outline-none" /></td>
                        ))}</tr>
                      ))}</tbody>
                    </table>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button onClick={() => { const id = `col-${Date.now()}`; handleChange({ table: { ...table, columns: [...table.columns, { id, name: `Column ${table.columns.length + 1}` }] } }); }} className="px-3 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium">+ Column</button>
                    <button onClick={() => handleChange({ table: { ...table, rows: [...table.rows, { id: `row-${Date.now()}`, cells: {} }] } })} className="px-3 py-2 rounded-lg bg-gray-100 text-gray-600 text-sm font-medium">+ Row</button>
                  </div>
                </div>
              )}
            </div>

            {(localNote.attachments || []).length > 0 && (
              <div className="mt-8 border-t border-gray-100 pt-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs font-bold uppercase tracking-widest text-gray-400">Attachments</h3>
                  <span className="text-xs text-gray-400">{localNote.attachments.length} attached</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {localNote.attachments.map(att => att.type === 'image' ? (
                    <div key={att.id} className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50 group">
                      <div className="relative bg-gray-100">
                        <img src={att.dataRef || att.driveUrl} alt={att.caption || att.name || 'Note attachment'} className="w-full max-h-72 object-contain" style={{ transform: `rotate(${att.rotation || 0}deg)` }} />
                        <div className="absolute top-2 right-2 flex gap-1 md:opacity-0 md:group-hover:opacity-100 transition">
                          <button onClick={() => downloadAttachment(att)} className="p-2 rounded-lg bg-white/90 shadow text-blue-600" title="Download image"><SafeIcon name="Download" /></button>
                          <button onClick={() => updateAttachment(att.id, { rotation: ((att.rotation || 0) + 90) % 360 })} className="p-2 rounded-lg bg-white/90 shadow text-gray-600" title="Rotate"><SafeIcon name="RotateCw" /></button>
                          <button onClick={() => removeAttachment(att.id)} className="p-2 rounded-lg bg-white/90 shadow text-red-500" title="Remove image"><SafeIcon name="Trash2" /></button>
                        </div>
                      </div>
                      <div className="px-3 py-2 bg-white border-t border-gray-100">
                        <div className="text-xs font-medium text-gray-600 truncate">{att.name || 'Screenshot'}</div>
                        <input value={att.caption || ''} onChange={e => updateAttachment(att.id, { caption: e.target.value })} placeholder="Add a caption…" className="w-full mt-1 bg-transparent outline-none text-sm text-gray-500" />
                      </div>
                    </div>
                  ) : (
                    <div key={att.id} className="border border-gray-200 rounded-xl bg-white p-4 flex items-center gap-3 group">
                      <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0"><SafeIcon name="FileText" /></div>
                      <div className="min-w-0 flex-1 text-left">
                        <div className="font-bold text-sm text-gray-800 truncate">{att.name || 'Attachment'}</div>
                        <div className="text-[11px] text-gray-400 truncate">{formatFileSize(att.size)}{att.mimeType ? ` · ${att.mimeType}` : ''}</div>
                      </div>
                      <button onClick={() => downloadAttachment(att)} className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition" title="Download attachment" aria-label={`Download ${att.name || 'attachment'}`}><SafeIcon name="Download" /></button>
                      <button onClick={() => removeAttachment(att.id)} className="p-2 text-gray-300 hover:text-red-500 md:opacity-0 md:group-hover:opacity-100 transition" title="Remove attachment"><SafeIcon name="Trash2" /></button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default Editor;